/**
 * CartSummary - Example Component Using UI Core
 *
 * This component demonstrates how to use @kidslabs/mario-nextjs-react-ui-core
 * components in Nova pages.
 *
 * Pattern:
 * 1. Import UI Core components for UI primitives (Button, Text, Card, etc.)
 * 2. Use CSS modules for layout and custom styling
 * 3. Use Nova theme tokens via CSS variables
 */

import React from 'react';
import { cn, Button, Text, Card, Divider, Badge } from '@kidslabs/mario-nextjs-react-ui-core';
import styles from './cart-summary.module.css';

interface CartItem {
    id: string;
    name: string;
    price: number;
    quantity: number;
    image?: string;
}

interface CartSummaryProps {
    items: CartItem[];
    subtotal: number;
    shipping: number;
    discount?: number;
    onCheckout: () => void;
    onContinueShopping: () => void;
    isLoading?: boolean;
}

/**
 * CartSummary Component
 *
 * Uses UI Core components for consistent design system
 * while applying Nova-specific theming via CSS modules.
 */
export const CartSummary: React.FC<CartSummaryProps> = ({
    items,
    subtotal,
    shipping,
    discount = 0,
    onCheckout,
    onContinueShopping,
    isLoading = false,
}) => {
    const total = subtotal + shipping - discount;
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <Card variant="outlined" padding="none" className={styles.summaryCard}>
            {/* Header */}
            <div className={styles.header}>
                <Text variant="h3" size="medium" weight="bold">
                    Tóm tắt đơn hàng
                </Text>
                <Badge variant="primary">{itemCount} sản phẩm</Badge>
            </div>

            <Divider />

            {/* Items List */}
            <div className={styles.itemsList}>
                {items.map(item => (
                    <div key={item.id} className={styles.itemRow}>
                        <div className={styles.itemInfo}>
                            <Text variant="body" size="small" weight="medium">
                                {item.name}
                            </Text>
                            <Text variant="body" size="xsmall" className={styles.quantityText}>
                                x{item.quantity}
                            </Text>
                        </div>
                        <Text variant="body" size="small" weight="bold" className={styles.priceText}>
                            {(item.price * item.quantity).toLocaleString('vi-VN')}đ
                        </Text>
                    </div>
                ))}
            </div>

            <Divider />

            {/* Price Summary */}
            <div className={styles.priceSummary}>
                <div className={styles.priceRow}>
                    <Text variant="body" size="small">
                        Tạm tính
                    </Text>
                    <Text variant="body" size="small">
                        {subtotal.toLocaleString('vi-VN')}đ
                    </Text>
                </div>

                <div className={styles.priceRow}>
                    <Text variant="body" size="small">
                        Phí vận chuyển
                    </Text>
                    <Text variant="body" size="small">
                        {shipping === 0 ? 'Miễn phí' : `${shipping.toLocaleString('vi-VN')}đ`}
                    </Text>
                </div>

                {discount > 0 && (
                    <div className={styles.priceRow}>
                        <Text variant="body" size="small" className={styles.discountLabel}>
                            Giảm giá
                        </Text>
                        <Text variant="body" size="small" className={styles.discountValue}>
                            -{discount.toLocaleString('vi-VN')}đ
                        </Text>
                    </div>
                )}

                <Divider />

                <div className={cn(styles.priceRow, styles.totalRow)}>
                    <Text variant="h3" size="medium" weight="bold">
                        Tổng cộng
                    </Text>
                    <Text variant="h3" size="medium" weight="bold" className={styles.totalPrice}>
                        {total.toLocaleString('vi-VN')}đ
                    </Text>
                </div>
            </div>

            {/* Actions */}
            <div className={styles.actions}>
                <Button
                    variant="contained"
                    colorRole="primary"
                    size="large"
                    fullWidth
                    onClick={onCheckout}
                    loading={isLoading}
                >
                    Tiến hành thanh toán
                </Button>

                <Button variant="text" colorRole="primary" size="medium" fullWidth onClick={onContinueShopping}>
                    Tiếp tục mua sắm
                </Button>
            </div>
        </Card>
    );
};

export default CartSummary;
